const MIN_AMOUNT = 1;
const MAX_AMOUNT = 999;

class SupportWidget extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-support-form]');
    this.amountButtons = Array.from(this.querySelectorAll('[data-amount-button]'));
    this.customInput = this.querySelector('[data-amount-input]');
    this.errorEl = this.querySelector('[data-support-error]');
    this.submitButton = this.querySelector('[data-support-submit]');
    this.messageInput = this.querySelector('[data-message-input]');
    this.messageCount = this.querySelector('[data-message-count]');
    this.anonymousCheckbox = this.querySelector('[data-anonymous-checkbox]');
    this.nameInput = this.querySelector('[data-name-input]');
    this.emailInput = this.querySelector('[data-email-input]');
    this.variantId = this.dataset.variantId;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.amountButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectPresetAmount(button), { signal });
    });

    if (this.customInput) {
      this.customInput.addEventListener('input', () => this.handleCustomInput(), { signal });
      this.customInput.addEventListener('blur', () => this.clampCustomInput(), { signal });
    }

    if (this.messageInput && this.messageCount) {
      this.messageInput.addEventListener('input', () => this.updateMessageCount(), { signal });
      this.updateMessageCount();
    }

    if (this.anonymousCheckbox) {
      this.anonymousCheckbox.addEventListener('change', () => this.toggleAnonymous(), { signal });
    }

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  selectPresetAmount(button) {
    this.amountButtons.forEach((b) => b.classList.toggle('is-selected', b === button));
    if (this.customInput) {
      this.customInput.value = button.dataset.amountButton;
    }
    this.clearError();
  }

  handleCustomInput() {
    const value = this.customInput.value;
    this.amountButtons.forEach((b) => {
      b.classList.toggle('is-selected', b.dataset.amountButton === value);
    });
    this.clearError();
  }

  clampCustomInput() {
    if (!this.customInput.value) return;
    let value = Number(this.customInput.value);
    if (Number.isNaN(value)) {
      this.customInput.value = '';
      return;
    }
    value = Math.min(Math.max(Math.round(value), MIN_AMOUNT), MAX_AMOUNT);
    this.customInput.value = value;
  }

  updateMessageCount() {
    const length = this.messageInput.value.length;
    this.messageCount.textContent = `${length} / 300`;
  }

  toggleAnonymous() {
    const isAnonymous = this.anonymousCheckbox.checked;
    if (this.nameInput) this.nameInput.disabled = isAnonymous;
    if (this.emailInput) this.emailInput.disabled = isAnonymous;
  }

  clearError() {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    const amount = Number(this.customInput?.value);
    if (!amount || Number.isNaN(amount) || amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
      this.showError(`Please choose or enter an amount between $${MIN_AMOUNT} and $${MAX_AMOUNT}.`);
      return;
    }

    if (!this.variantId) {
      this.showError('This channel is not available for support yet.');
      return;
    }

    const properties = {};
    const isAnonymous = this.anonymousCheckbox?.checked;
    if (!isAnonymous && this.nameInput?.value) properties['Name'] = this.nameInput.value;
    if (!isAnonymous && this.emailInput?.value) properties['Email'] = this.emailInput.value;
    if (this.messageInput?.value) properties['Message'] = this.messageInput.value;
    properties['Anonymous'] = isAnonymous ? 'Yes' : 'No';

    this.submitButton.disabled = true;
    this.submitButton.classList.add('is-loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: this.variantId,
              quantity: amount,
              properties,
            },
          ],
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || 'Something went wrong. Please try again.');
      }

      window.location.href = '/checkout';
    } catch (error) {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('is-loading');
      this.showError(error.message || 'Something went wrong. Please try again.');
    }
  }
}

if (!customElements.get('support-widget')) {
  customElements.define('support-widget', SupportWidget);
}
